import { forwardRef, useState } from 'react'
import { services, Service } from '../data/services'
import './Services.css'

interface ServicesProps {
  id: string
}

const Services = forwardRef<HTMLDivElement, ServicesProps>(({ id }, ref) => {
  const [expandedService, setExpandedService] = useState<string | null>(null)

  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId)
  }

  return (
    <section
      ref={ref}
      id={id}
      className="services section"
      aria-labelledby="services-title"
    >
      <div className="container">
        <header className="section__header animate-in">
          <h2 id="services-title" className="section__title">
            Mina <span className="text-terracotta">behandlingar</span>
          </h2>
          <p className="section__subtitle">
            Läs om de olika behandlingsformer jag erbjuder på mottagningen i Malmö eller online.
          </p>
        </header>

        <div className="services__intro animate-in animate-delay-1" aria-hidden="true">
          <img
            src="/images/services-leaves.jpg"
            alt=""
            className="services__intro-image"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="services__grid grid grid--3">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              isExpanded={expandedService === service.id}
              onToggle={() => toggleService(service.id)}
              delay={index}
            />
          ))}
        </div>

        <div className="services__cta animate-in">
          <p className="services__cta-text">
            Osäker på vad som passar dig? <a href="#contact" className="services__cta-link">Kontakta mig</a> på telefon eller mejl för ett första möte.
          </p>
        </div>
      </div>
    </section>
  )
})

Services.displayName = 'Services'

export default Services

interface ServiceCardProps {
  service: Service
  isExpanded: boolean
  onToggle: () => void
  delay: number
}

function ServiceCard({ service, isExpanded, onToggle, delay }: ServiceCardProps) {
  return (
    <article
      className={`card service-card ${isExpanded ? 'service-card--expanded' : ''} animate-in animate-delay-${Math.min(delay + 1, 4)}`}
      style={{ '--delay': `${delay * 100}ms` } as React.CSSProperties}
    >
      <div className="service-card__header" onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onToggle()} aria-expanded={isExpanded}>
        <div className="service-card__title-group">
          <h3 className="service-card__title">{service.title}</h3>
          <span className="service-card__expand-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>
      </div>

      <div className="service-card__content">
        <p className="service-card__description">{service.description}</p>

        {isExpanded && (
          <div className="service-card__expanded">
            {service.sections.map((section, i) => (
              <div key={i} className="service-card__section">
                {section.heading && <h4 className="service-card__section-heading">{section.heading}</h4>}
                <p className="service-card__long-description">{section.text}</p>
              </div>
            ))}

            <a href="#contact" className="btn btn--primary service-card__book-btn" data-section="contact">
              Kontakta mig
            </a>
          </div>
        )}
      </div>
    </article>
  )
}